import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { requestJson } from "../../../../helpers/api";
import type { CajaDiaAnterior, SucursalRendicion } from "../../RendicionCaja/types";

type TipoEmision = "manual" | "electronica";
type CondicionPago = "contado" | "credito";

type ItemCompra = {
  [key: string]: any;

  id: number;
  descripcion: string;
  cantidad: number;
  costoUnitario: number;
  iva: "10" | "5" | "0";
  articuloId?: string | null;
  articuloReferencia?: string | null;
};

type ProveedorBusquedaResultado = {
  idpersona: number | null;
  persona: string | null;
  razonSocial: string | null;
  nombre: string | null;
  apellido: string | null;
  documento: string | null;
  ruc: string | null;
  rucNumero: string | null;
  dv: string | null;
  telefono: string | null;
  email: string | null;
};

type ArticuloBusquedaResultado = {
  idarticulo: string | null;
  descripcion: string | null;
  descripcionCorta: string | null;
  referencia: string | null;
  barcode: string | null;
  costoPromedio: number | null;
  precioSugerido: number | null;
  idImpuesto: number | null;
  idImpuestoCompra: number | null;
};

type ArticuloHistorialProveedor = {
  idarticulo: string | null;
  descripcion: string | null;
  unidadMedida: string | null;
  costoUnitarioSinIva: number | null;
  costoUnitarioConIva: number | null;
  importeTotal: number | null;
  cantidad: number | null;
  fecha: string | null;
  numeroFactura: string | null;
  serverOrigen: string | null;
  barcode: string | null;
  idImpuesto: number | null;
  idImpuestoCompra: number | null;
};

type ControlCajaInfo = {
  sucursalId?: number | null;
  sucursalNombre?: string | null;
  sucursalEtiqueta?: string | null;
  turnoId?: number | null;
  turnoDescripcion?: string | null;
  cajeroId?: number | null;
  cajeroNombre?: string | null;
  fechaApertura?: string | null;
  fechaCierre?: string | null;
  fechaHoraAperturaTurno?: string | null;
  estadoCaja?: string | null;
  estadoTurno?: string | null;
};

type ListadoFiltrosConsulta = {
  fechaDesde?: string;
  fechaHasta?: string;
  sucursalId?: string;
  cajero?: string;
  proveedor?: string;
  search?: string;
  limit: number;
};

type RegistroCompraListadoDto = {
  id: number;
  proveedor: string | null;
  numeroDocumento: string;
  total: number;
  moneda: string;
  condicionPago: string;
  createdAt: string;
  controlCajaId: number | null;
  usuarioId: number | null;
  usuarioNombre: string | null;
  timbrado: string | null;
  timbradoVencimiento?: string | null;
  itemsCantidad?: number;
  totalGravada10?: number;
  totalGravada5?: number;
  totalExenta?: number;
  fechaEmision?: string | null;
  fechaRecepcion?: string | null;
  proveedorDocumento: string | null;
  estado?: string;
  controlCajaInfo: ControlCajaInfo | null;
};

const crearFechasIniciales = () => {
  const hoy = new Date();
  const haceTresMeses = new Date(hoy);
  haceTresMeses.setMonth(hoy.getMonth() - 3);
  const formatear = (fecha: Date) => fecha.toISOString().slice(0, 10);
  return { desde: formatear(haceTresMeses), hasta: formatear(hoy) };
};

const inferirIvaDesdeArticulo = (registro: ArticuloBusquedaResultado): ItemCompra["iva"] => {
  const candidatos = [registro.idImpuestoCompra, registro.idImpuesto].filter(
    (valor): valor is number => typeof valor === "number" && Number.isFinite(valor),
  );
  if (candidatos.some((valor) => valor === 3)) {
    return "0";
  }
  if (candidatos.some((valor) => valor === 2 || valor === 5)) {
    return "5";
  }
  return "10";
};

const MONEDAS = [
  { value: "PYG", label: "Guaraníes (PYG)" },
  { value: "USD", label: "Dólares (USD)" },
];

const crearItem = (): ItemCompra => ({
  id: Date.now(),
  descripcion: "",
  cantidad: 1,
  costoUnitario: 0,
  iva: "10",
  articuloId: null,
  articuloReferencia: null,
});

const calcularTotales = (items: ItemCompra[]) =>
  items.reduce(
    (acc, item) => {
      const base = item.cantidad * item.costoUnitario;
      if (item.iva === "10") {
        acc.gravada10 += base;
        acc.iva10 += base * (10 / 110);
      } else if (item.iva === "5") {
        acc.gravada5 += base;
        acc.iva5 += base * (5 / 105);
      } else {
        acc.exenta += base;
      }
      acc.total += base;
      return acc;
    },
    { gravada10: 0, gravada5: 0, exenta: 0, iva10: 0, iva5: 0, total: 0 },
  );

const describirCaja = (caja: CajaDiaAnterior) => {
  const partes = [
    `Caja #${caja.idcontrolcaja}`,
    caja.sucursal ?? caja.sucursalNombre ?? (caja.idsucursal != null ? `Sucursal ${caja.idsucursal}` : null),
    caja.turno ?? (caja.idturno != null ? `Turno ${caja.idturno}` : null),
    caja.cajero ?? (caja.idcajero != null ? `Cajero ${caja.idcajero}` : null),
  ].filter(Boolean);
  const fechaReferencia = caja.fechaApertura ?? caja.fechaHoraAperturaTurno ?? caja.fechaCierre ?? null;
  const fechaTexto = fechaReferencia ? new Date(fechaReferencia).toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" }) : null;
  if (fechaTexto) {
    partes.push(fechaTexto);
  }
  return partes.join(" · ");
};

interface RegistroComprasStepperProps {
  cajasDisponibles?: CajaDiaAnterior[];
  cargandoCajas?: boolean;
  errorCajas?: string | null;
  onRecargarCajas?: () => void;
  sucursalesDisponibles?: SucursalRendicion[];
  cargandoSucursales?: boolean;
  errorSucursales?: string | null;
  onRecargarSucursales?: () => void;
}

const RegistroComprasStepper = ({
  cajasDisponibles = [],
  cargandoCajas = false,
  errorCajas = null,
  onRecargarCajas,
  sucursalesDisponibles = [],
  cargandoSucursales = false,
  errorSucursales = null,
  onRecargarSucursales,
}: RegistroComprasStepperProps) => {
  const [tipoEmision, setTipoEmision] = useState<TipoEmision>("manual");
  const [condicionPago, setCondicionPago] = useState<CondicionPago>("contado");
  const [proveedorRuc, setProveedorRuc] = useState("");
  const [proveedorDv, setProveedorDv] = useState("");
  const [proveedorNombre, setProveedorNombre] = useState("");
  const [proveedorEmail, setProveedorEmail] = useState("");
  const [proveedorTelefono, setProveedorTelefono] = useState("");
  const [numeroFactura, setNumeroFactura] = useState({ establecimiento: "", punto: "", numero: "" });
  const [cdc, setCdc] = useState("");
  const [timbrado, setTimbrado] = useState("");
  const [timbradoVencimiento, setTimbradoVencimiento] = useState("");
  const [fechaEmision, setFechaEmision] = useState("");
  const [fechaRecepcion, setFechaRecepcion] = useState("");
  const [moneda, setMoneda] = useState("PYG");
  const [tipoCambio, setTipoCambio] = useState("1");
  const [items, setItems] = useState<ItemCompra[]>([crearItem()]);
  const [adjuntos, setAdjuntos] = useState<File[]>([]);
  const [comentario, setComentario] = useState("");
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [controlCajaSeleccionada, setControlCajaSeleccionada] = useState("");
  const [sucursalFiltro, setSucursalFiltro] = useState("");
  const [aplicaRendicion, setAplicaRendicion] = useState(false);
  const [aplicaFondo, setAplicaFondo] = useState(false);
  const [montoRendicion, setMontoRendicion] = useState("");
  const [montoFondo, setMontoFondo] = useState("");
  const [busquedaProveedor, setBusquedaProveedor] = useState("");
  const [proveedorId, setProveedorId] = useState<number | null>(null);
  const [resultadosProveedor, setResultadosProveedor] = useState<ProveedorBusquedaResultado[]>([]);
  const [buscandoProveedor, setBuscandoProveedor] = useState(false);
  const [errorBusquedaProveedor, setErrorBusquedaProveedor] = useState<string | null>(null);
  const [modalProveedorAbierto, setModalProveedorAbierto] = useState(false);
  const [modalArticuloAbierto, setModalArticuloAbierto] = useState(false);
  const [itemObjetivoArticulo, setItemObjetivoArticulo] = useState<number | null>(null);
  const [busquedaArticulo, setBusquedaArticulo] = useState("");
  const [resultadosArticulo, setResultadosArticulo] = useState<ArticuloBusquedaResultado[]>([]);
  const [buscandoArticulo, setBuscandoArticulo] = useState(false);
  const [errorBusquedaArticulo, setErrorBusquedaArticulo] = useState<string | null>(null);
  const [modalCalculadoraAbierto, setModalCalculadoraAbierto] = useState(false);
  const [calculadoraItemId, setCalculadoraItemId] = useState<number | null>(null);
  const [calculadoraCantidad, setCalculadoraCantidad] = useState("");
  const [calculadoraTotal, setCalculadoraTotal] = useState("");
  const [errorCalculadora, setErrorCalculadora] = useState<string | null>(null);
  const [modalExitoAbierto, setModalExitoAbierto] = useState(false);
  const [registroCreadoId, setRegistroCreadoId] = useState<number | null>(null);
  const [articulosRecientes, setArticulosRecientes] = useState<ArticuloHistorialProveedor[]>([]);
  const [cargandoArticulosRecientes, setCargandoArticulosRecientes] = useState(false);
  const [errorArticulosRecientes, setErrorArticulosRecientes] = useState<string | null>(null);
  const [mostrarHistorialCompleto, setMostrarHistorialCompleto] = useState(false);
  const [modalAviso, setModalAviso] = useState<{ titulo: string; mensaje: string } | null>(null);
  const [tabActiva, setTabActiva] = useState<"registrar" | "lista">("registrar");
  const [modalFormularioAbierto, setModalFormularioAbierto] = useState(false);
  const [facturasRegistradas, setFacturasRegistradas] = useState<RegistroCompraListadoDto[]>([]);
  const [cargandoFacturas, setCargandoFacturas] = useState(false);
  const [errorFacturas, setErrorFacturas] = useState<string | null>(null);
  const fechasIniciales = useMemo(() => crearFechasIniciales(), []);
  const [filtroListadoFechaDesde, setFiltroListadoFechaDesde] = useState(fechasIniciales.desde);
  const [filtroListadoFechaHasta, setFiltroListadoFechaHasta] = useState(fechasIniciales.hasta);
  const [filtroListadoSucursalId, setFiltroListadoSucursalId] = useState("");
  const [filtroListadoCajero, setFiltroListadoCajero] = useState("");
  const [filtroListadoProveedor, setFiltroListadoProveedor] = useState("");
  const [filtroListadoBusqueda, setFiltroListadoBusqueda] = useState("");
  const [filtroListadoLimite, setFiltroListadoLimite] = useState("200");
  const [filtrosConsulta, setFiltrosConsulta] = useState<ListadoFiltrosConsulta>(() => ({
    fechaDesde: fechasIniciales.desde,
    fechaHasta: fechasIniciales.hasta,
    sucursalId: undefined,
    cajero: undefined,
    proveedor: undefined,
    search: undefined,
    limit: 200,
  }));

  const cajeroFiltroOpciones = useMemo(() => {
    const valores = new Set<string>();
    facturasRegistradas.forEach((factura) => {
      const nombre = factura.controlCajaInfo?.cajeroNombre?.trim();
      if (nombre) {
        valores.add(nombre);
      }
    });
    return Array.from(valores).sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));
  }, [facturasRegistradas]);

  const proveedorFiltroOpciones = useMemo(() => {
    const mapa = new Map<string, string>();
    facturasRegistradas.forEach((factura) => {
      const valor = (factura.proveedorDocumento ?? factura.proveedor ?? "").trim();
      if (!valor) {
        return;
      }
      const etiqueta =
        factura.proveedorDocumento && factura.proveedor
          ? `${factura.proveedor} · ${factura.proveedorDocumento}`
          : factura.proveedor ?? factura.proveedorDocumento ?? valor;
      if (!mapa.has(valor)) {
        mapa.set(valor, etiqueta);
      }
    });
    return Array.from(mapa.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "es", { sensitivity: "base" }));
  }, [facturasRegistradas]);

  const totales = useMemo(() => calcularTotales(items), [items]);
  const totalFactura = useMemo(() => {
    const totalNumero = Number(totales.total);
    if (!Number.isFinite(totalNumero) || totalNumero <= 0) {
      return 0;
    }
    return totalNumero;
  }, [totales.total]);
  const montoRendicionNumero = Number(montoRendicion) || 0;
  const montoFondoNumero = Number(montoFondo) || 0;
  const maximoMontoRendicion = Math.max(totalFactura - (aplicaFondo ? montoFondoNumero : 0), 0);
  const maximoMontoFondo = Math.max(totalFactura - (aplicaRendicion ? montoRendicionNumero : 0), 0);

  const numeroFacturaValido =
    numeroFactura.establecimiento.trim().length === 3 &&
    numeroFactura.punto.trim().length === 3 &&
    numeroFactura.numero.trim().length === 7;
  const numeroFacturaCadena = useMemo(() => {
    const partes = [numeroFactura.establecimiento, numeroFactura.punto, numeroFactura.numero].filter(
      (parte) => typeof parte === "string" && parte.trim().length > 0,
    );
    if (partes.length === 0) {
      return "";
    }
    return partes.join("-");
  }, [numeroFactura]);

  const actualizarNumeroFacturaCadena = useCallback((valor: string) => {
    const soloDigitos = valor.replace(/\D/g, "").slice(0, 13);
    setNumeroFactura({
      establecimiento: soloDigitos.slice(0, 3),
      punto: soloDigitos.slice(3, 6),
      numero: soloDigitos.slice(6, 13),
    });
  }, []);

  const controlCajaNumero = Number(controlCajaSeleccionada.trim());
  const controlCajaNumeroValido = Number.isFinite(controlCajaNumero) && controlCajaNumero > 0;
  const requiereControlCaja = aplicaRendicion || aplicaFondo;
  const esMonedaLocal = moneda === "PYG";
  const formatearMonto = useCallback((valor: number) => {
    if (!Number.isFinite(valor)) {
      return "0";
    }
    return valor.toLocaleString("es-PY", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }, []);
  const totalAsignado =
    (aplicaRendicion ? montoRendicionNumero : 0) + (aplicaFondo ? montoFondoNumero : 0);
  const excesoAsignacion = totalFactura > 0 && totalAsignado > totalFactura;
  const sucursalOptions = useMemo(
    () =>
      [
        { value: "", label: "Todas las sucursales", descripcionFuente: null },
        ...sucursalesDisponibles.map((sucursal) => ({
          value: String(sucursal.idsucursal),
          label: sucursal.sucursalNombre ?? `Sucursal ${sucursal.idsucursal}`,
          descripcionFuente: sucursal.fuente ?? null,
        })),
      ],
    [sucursalesDisponibles],
  );

  const opcionesCajas = useMemo(() => {
    return cajasDisponibles
      .filter((caja) => {
        if (!Number.isFinite(caja.idcontrolcaja)) {
          return false;
        }
        if (!sucursalFiltro) {
          return true;
        }
        const id = caja.idsucursal != null ? String(caja.idsucursal) : "";
        return id === sucursalFiltro;
      })
      .sort((a, b) => {
        const fechaA = new Date(a.fechaApertura ?? a.fechaHoraAperturaTurno ?? 0).getTime();
        const fechaB = new Date(b.fechaApertura ?? b.fechaHoraAperturaTurno ?? 0).getTime();
        return fechaB - fechaA;
      })
      .map((caja) => ({
        value: String(caja.idcontrolcaja),
        label: describirCaja(caja),
      }));
  }, [cajasDisponibles, sucursalFiltro]);

  const cajaSeleccionadaInfo = useMemo(() => {
    if (!controlCajaSeleccionada) {
      return null;
    }
    return (
      cajasDisponibles.find(
        (caja) => String(caja.idcontrolcaja) === controlCajaSeleccionada,
      ) ?? null
    );
  }, [cajasDisponibles, controlCajaSeleccionada]);

  const sucursalBusquedaId = useMemo(() => {
    if (cajaSeleccionadaInfo?.idsucursal && Number.isFinite(cajaSeleccionadaInfo.idsucursal)) {
      return Number(cajaSeleccionadaInfo.idsucursal);
    }
    const id = Number(sucursalFiltro);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [cajaSeleccionadaInfo, sucursalFiltro]);

  const sucursalBusquedaLabel = useMemo(() => {
    if (cajaSeleccionadaInfo?.sucursalNombre) {
      return cajaSeleccionadaInfo.sucursalNombre;
    }
    if (cajaSeleccionadaInfo?.sucursal) {
      return cajaSeleccionadaInfo.sucursal;
    }
    if (!sucursalBusquedaId) {
      return null;
    }
    const encontrada = sucursalesDisponibles.find(
      (sucursal) => sucursal.idsucursal === sucursalBusquedaId,
    );
    return encontrada?.sucursalNombre ?? null;
  }, [cajaSeleccionadaInfo, sucursalBusquedaId, sucursalesDisponibles]);

  const sucursalBusquedaFuente = useMemo(() => {
    if (!sucursalBusquedaId) {
      return null;
    }
    const encontrada = sucursalesDisponibles.find(
      (sucursal) => sucursal.idsucursal === sucursalBusquedaId,
    );
    return encontrada?.fuente ?? null;
  }, [sucursalesDisponibles, sucursalBusquedaId]);
  const permiteBusquedaProveedor = Boolean(sucursalBusquedaId);
  const permiteBusquedaArticulo = Boolean(sucursalBusquedaId && sucursalBusquedaFuente);
  const descripcionFuenteBusqueda = useMemo(() => {
    if (!sucursalBusquedaId) {
      return null;
    }
    const nombreSucursal = sucursalBusquedaLabel ?? `Sucursal ${sucursalBusquedaId}`;
    if (sucursalBusquedaFuente) {
      return `la base ${sucursalBusquedaFuente}${
        nombreSucursal ? ` (${nombreSucursal})` : ""
      }`;
    }
    return `la sucursal ${nombreSucursal}`;
  }, [sucursalBusquedaFuente, sucursalBusquedaId, sucursalBusquedaLabel]);
  const descripcionFuenteSeleccionada = useMemo(() => {
    if (!sucursalFiltro) {
      return null;
    }
    const encontrada = sucursalOptions.find((opcion) => opcion.value === sucursalFiltro);
    return encontrada?.descripcionFuente ?? null;
  }, [sucursalFiltro, sucursalOptions]);

  useEffect(() => {
    if (!controlCajaSeleccionada) {
      return;
    }
    const existe = opcionesCajas.some((opcion) => opcion.value === controlCajaSeleccionada);
    if (!existe) {
      setControlCajaSeleccionada("");
    }
  }, [controlCajaSeleccionada, opcionesCajas]);

  useEffect(() => {
    if (!controlCajaNumeroValido) {
      if (aplicaRendicion) {
        setAplicaRendicion(false);
      }
      if (aplicaFondo) {
        setAplicaFondo(false);
      }
      if (montoRendicion) {
        setMontoRendicion("");
      }
      if (montoFondo) {
        setMontoFondo("");
      }
    }
  }, [controlCajaNumeroValido, aplicaRendicion, aplicaFondo, montoRendicion, montoFondo]);

  useEffect(() => {
    const termino = busquedaProveedor.trim();
    if (!modalProveedorAbierto) {
      setResultadosProveedor([]);
      setErrorBusquedaProveedor(null);
      setBuscandoProveedor(false);
      return;
    }
    if (!termino) {
      setResultadosProveedor([]);
      setErrorBusquedaProveedor(null);
      setBuscandoProveedor(false);
      return;
    }
    if (!sucursalBusquedaId || !sucursalBusquedaFuente) {
      setResultadosProveedor([]);
      setErrorBusquedaProveedor("Seleccioná una sucursal o caja para habilitar el buscador de proveedores.");
      setBuscandoProveedor(false);
      return;
    }
    if (termino.length < 3) {
      setResultadosProveedor([]);
      setErrorBusquedaProveedor("Escribí al menos 3 caracteres para buscar.");
      setBuscandoProveedor(false);
      return;
    }
    let cancelado = false;
    setBuscandoProveedor(true);
    setErrorBusquedaProveedor(null);
    const controller = new AbortController();
    const params = new URLSearchParams({
      termino,
      sucursalId: String(sucursalBusquedaId),
      fuente: sucursalBusquedaFuente,
    });
    const timeoutId = window.setTimeout(() => {
      requestJson<ProveedorBusquedaResultado[]>(`/api/compras/proveedores?${params.toString()}`, {
        signal: controller.signal,
      })
        .then((data) => {
          if (cancelado) {
            return;
          }
          setResultadosProveedor(Array.isArray(data) ? data : []);
        })
        .catch((err) => {
          if (cancelado || err.name === "AbortError") {
            return;
          }
          setErrorBusquedaProveedor(err instanceof Error ? err.message : "No pudimos buscar proveedores.");
          setResultadosProveedor([]);
        })
        .finally(() => {
          if (cancelado) {
            return;
          }
          setBuscandoProveedor(false);
        });
    }, 400);
    return () => {
      cancelado = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [busquedaProveedor, sucursalBusquedaId, sucursalBusquedaFuente, modalProveedorAbierto]);

  useEffect(() => {
    const termino = busquedaArticulo.trim();
    if (!modalArticuloAbierto) {
      setResultadosArticulo([]);
      setErrorBusquedaArticulo(null);
      setBuscandoArticulo(false);
      return;
    }
    if (!termino) {
      setResultadosArticulo([]);
      setErrorBusquedaArticulo(null);
      setBuscandoArticulo(false);
      return;
    }
    if (!sucursalBusquedaId || !sucursalBusquedaFuente) {
      setResultadosArticulo([]);
      setErrorBusquedaArticulo("Seleccioná una sucursal o caja para habilitar la búsqueda.");
      setBuscandoArticulo(false);
      return;
    }
    if (termino.length < 3) {
      setResultadosArticulo([]);
      setErrorBusquedaArticulo("Escribí al menos 3 caracteres para buscar.");
      setBuscandoArticulo(false);
      return;
    }
    let cancelado = false;
    setBuscandoArticulo(true);
    setErrorBusquedaArticulo(null);
    const controller = new AbortController();
    const params = new URLSearchParams({
      termino,
      sucursalId: String(sucursalBusquedaId),
      fuente: sucursalBusquedaFuente,
    });
    const timeoutId = window.setTimeout(() => {
      requestJson<ArticuloBusquedaResultado[]>(`/api/compras/articulos?${params.toString()}`, {
        signal: controller.signal,
      })
        .then((data) => {
          if (cancelado) {
            return;
          }
          setResultadosArticulo(Array.isArray(data) ? data : []);
        })
        .catch((err) => {
          if (cancelado || err.name === "AbortError") {
            return;
          }
          setErrorBusquedaArticulo(err instanceof Error ? err.message : "No pudimos buscar artículos.");
          setResultadosArticulo([]);
        })
        .finally(() => {
          if (cancelado) {
            return;
          }
          setBuscandoArticulo(false);
        });
    }, 300);
    return () => {
      cancelado = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [busquedaArticulo, modalArticuloAbierto, sucursalBusquedaFuente, sucursalBusquedaId]);

  useEffect(() => {
    if (!proveedorId || !permiteBusquedaArticulo || !sucursalBusquedaId || !sucursalBusquedaFuente) {
      setArticulosRecientes([]);
      setErrorArticulosRecientes(null);
      setCargandoArticulosRecientes(false);
      setMostrarHistorialCompleto(false);
      return;
    }
    setMostrarHistorialCompleto(false);
    let cancelado = false;
    setCargandoArticulosRecientes(true);
    setErrorArticulosRecientes(null);
    const params = new URLSearchParams({
      sucursalId: String(sucursalBusquedaId),
      fuente: sucursalBusquedaFuente,
      meses: "6",
      limite: "0",
    });
    requestJson<ArticuloHistorialProveedor[]>(
      `/api/compras/proveedores/${proveedorId}/articulos-recientes?${params.toString()}`,
    )
      .then((data) => {
        if (cancelado) {
          return;
        }
        setArticulosRecientes(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelado) {
          return;
        }
        setErrorArticulosRecientes(
          err instanceof Error ? err.message : "No pudimos obtener el historial de artículos.",
        );
        setArticulosRecientes([]);
      })
      .finally(() => {
        if (!cancelado) {
          setCargandoArticulosRecientes(false);
        }
      });
    return () => {
      cancelado = true;
    };
  }, [proveedorId, permiteBusquedaArticulo, sucursalBusquedaId, sucursalBusquedaFuente]);

  useEffect(() => {
    if (!aplicaRendicion && !aplicaFondo) {
      return;
    }
    if (totalFactura <= 0) {
      if (montoRendicion) {
        setMontoRendicion("");
      }
      if (montoFondo) {
        setMontoFondo("");
      }
      return;
    }
    const totalComoCadena = totalFactura.toString();
    if (aplicaRendicion && !aplicaFondo && montoRendicion !== totalComoCadena) {
      setMontoRendicion(totalComoCadena);
      return;
    }
    if (aplicaFondo && !aplicaRendicion && montoFondo !== totalComoCadena) {
      setMontoFondo(totalComoCadena);
      return;
    }
    if (aplicaRendicion && aplicaFondo) {
      const suma = montoRendicionNumero + montoFondoNumero;
      if (suma <= totalFactura) {
        return;
      }
      const exceso = suma - totalFactura;
      if (montoFondoNumero >= montoRendicionNumero && montoFondoNumero > 0) {
        const nuevoFondo = Math.max(montoFondoNumero - exceso, 0).toString();
        if (montoFondo !== nuevoFondo) {
          setMontoFondo(nuevoFondo);
        }
        return;
      }
      if (montoRendicionNumero > 0) {
        const nuevoRendicion = Math.max(montoRendicionNumero - exceso, 0).toString();
        if (montoRendicion !== nuevoRendicion) {
          setMontoRendicion(nuevoRendicion);
        }
      }
    }
  }, [
    aplicaRendicion,
    aplicaFondo,
    montoFondo,
    montoRendicion,
    montoRendicionNumero,
    montoFondoNumero,
    totalFactura,
  ]);

  const actualizarItem = useCallback((id: number, changes: Partial<ItemCompra>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)));
  }, []);

  const agregarItem = useCallback(() => {
    setItems((prev) => [...prev, crearItem()]);
  }, []);

  const eliminarItem = useCallback((id: number) => {
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((item) => item.id !== id)));
  }, []);

  const manejarAdjuntos = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    setAdjuntos(files ? Array.from(files) : []);
  }, []);

  const cerrarModalProveedor = useCallback(() => {
    setModalProveedorAbierto(false);
    setBusquedaProveedor("");
    setResultadosProveedor([]);
    setErrorBusquedaProveedor(null);
    setBuscandoProveedor(false);
  }, []);

  const seleccionarProveedor = useCallback((registro: ProveedorBusquedaResultado) => {
    const nombreCompuesto = [registro.nombre, registro.apellido]
      .filter((parte) => typeof parte === "string" && parte.trim().length > 0)
      .join(" ");
    const razon =
      registro.razonSocial && registro.razonSocial.trim().length > 0
        ? registro.razonSocial
        : registro.persona && registro.persona.trim().length > 0
        ? registro.persona
        : nombreCompuesto;
    const rucValor = registro.ruc && registro.ruc.trim().length > 0 ? registro.ruc : registro.rucNumero ?? "";
    setProveedorId(registro.idpersona ?? null);
    setProveedorNombre(razon ?? "");
    setProveedorRuc(rucValor ?? "");
    setProveedorDv(registro.dv ?? "");
    setProveedorEmail(registro.email ?? "");
    setProveedorTelefono(registro.telefono ?? "");
    cerrarModalProveedor();
  }, [cerrarModalProveedor]);

  const abrirModalArticulo = useCallback(
    (itemId: number | null) => {
      setItemObjetivoArticulo(itemId);
      setBusquedaArticulo("");
      setResultadosArticulo([]);
      setErrorBusquedaArticulo(null);
      setModalArticuloAbierto(true);
    },
    [],
  );

  const cerrarModalArticulo = useCallback(() => {
    setModalArticuloAbierto(false);
    setBusquedaArticulo("");
    setResultadosArticulo([]);
    setErrorBusquedaArticulo(null);
    setBuscandoArticulo(false);
  }, []);

  const seleccionarArticulo = useCallback(
    (registro: ArticuloBusquedaResultado) => {
      const objetivo = itemObjetivoArticulo ?? (items.length > 0 ? items[0].id : null);
      if (!objetivo) {
        cerrarModalArticulo();
        return;
      }
      const descripcion =
        registro.descripcion && registro.descripcion.trim().length > 0
          ? registro.descripcion
          : registro.descripcionCorta && registro.descripcionCorta.trim().length > 0
          ? registro.descripcionCorta
          : registro.referencia ?? "";
      const costoBase = Number(registro.costoPromedio ?? registro.precioSugerido ?? 0);
      const costo = Number.isFinite(costoBase) && costoBase > 0 ? costoBase : 0;
      const iva = inferirIvaDesdeArticulo(registro);
      actualizarItem(objetivo, {
        descripcion,
        costoUnitario: costo,
        iva,
        articuloId: registro.idarticulo ?? null,
        articuloReferencia: registro.referencia ?? registro.barcode ?? null,
      });
      cerrarModalArticulo();
    },
    [actualizarItem, cerrarModalArticulo, itemObjetivoArticulo, items],
  );

  const abrirCalculadora = useCallback(
    (item: ItemCompra) => {
      setCalculadoraItemId(item.id);
      setCalculadoraCantidad(String(item.cantidad ?? ""));
      const subtotal = (Number(item.cantidad) || 0) * (Number(item.costoUnitario) || 0);
      setCalculadoraTotal(subtotal ? String(subtotal) : "");
      setErrorCalculadora(null);
      setModalCalculadoraAbierto(true);
    },
    [],
  );

  const cerrarCalculadora = useCallback(() => {
    setModalCalculadoraAbierto(false);
    setCalculadoraItemId(null);
    setCalculadoraCantidad("");
    setCalculadoraTotal("");
    setErrorCalculadora(null);
  }, []);

  const aplicarCalculadora = useCallback(() => {
    if (!calculadoraItemId) {
      cerrarCalculadora();
      return;
    }
    const cantidadNumero = Number(calculadoraCantidad);
    const totalNumero = Number(calculadoraTotal);
    if (!Number.isFinite(cantidadNumero) || cantidadNumero <= 0) {
      setErrorCalculadora("Ingresá una cantidad válida mayor a 0.");
      return;
    }
    if (!Number.isFinite(totalNumero) || totalNumero <= 0) {
      setErrorCalculadora("Ingresá un total válido mayor a 0.");
      return;
    }
    const costo = totalNumero / cantidadNumero;
    actualizarItem(calculadoraItemId, {
      cantidad: Number(cantidadNumero.toFixed(4)),
      costoUnitario: Number(costo.toFixed(6)),
    });
    cerrarCalculadora();
  }, [actualizarItem, calculadoraCantidad, calculadoraItemId, calculadoraTotal, cerrarCalculadora]);

  const seleccionarArticuloDesdeHistorial = useCallback(
    (historial: ArticuloHistorialProveedor) => {
      const resultado: ArticuloBusquedaResultado = {
        idarticulo: historial.idarticulo,
        descripcion: historial.descripcion,
        descripcionCorta: null,
        referencia: historial.barcode,
        barcode: historial.barcode,
        costoPromedio: historial.costoUnitarioConIva ?? historial.costoUnitarioSinIva,
        precioSugerido: historial.costoUnitarioConIva ?? historial.costoUnitarioSinIva,
        idImpuesto: historial.idImpuesto,
        idImpuestoCompra: historial.idImpuestoCompra,
      };
      seleccionarArticulo(resultado);
    },
    [seleccionarArticulo],
  );

  const abrirFormulario = useCallback(() => {
    setMensaje(null);
    setError(null);
    setModalFormularioAbierto(true);
  }, []);

  const cerrarFormulario = useCallback(() => {
    if (!enviando) {
      setModalFormularioAbierto(false);
    }
  }, [enviando]);

  const filtrosConsultaQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", String(filtrosConsulta.limit));
    if (filtrosConsulta.fechaDesde) {
      params.set("fechaDesde", filtrosConsulta.fechaDesde);
    }
    if (filtrosConsulta.fechaHasta) {
      params.set("fechaHasta", filtrosConsulta.fechaHasta);
    }
    if (filtrosConsulta.sucursalId) {
      params.set("sucursalId", filtrosConsulta.sucursalId);
    }
    if (filtrosConsulta.cajero) {
      params.set("cajero", filtrosConsulta.cajero);
    }
    if (filtrosConsulta.proveedor) {
      params.set("proveedor", filtrosConsulta.proveedor);
    }
    if (filtrosConsulta.search) {
      params.set("search", filtrosConsulta.search);
    }
    return params.toString();
  }, [filtrosConsulta]);

  const cargarFacturasRegistradas = useCallback(() => {
    setCargandoFacturas(true);
    setErrorFacturas(null);
    const query = filtrosConsultaQuery ? `?${filtrosConsultaQuery}` : "";
    requestJson<RegistroCompraListadoDto[]>(`/api/compras/registros${query}`)
      .then((data) => {
        setFacturasRegistradas(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        setErrorFacturas(err instanceof Error ? err.message : "No pudimos obtener las facturas registradas.");
        setFacturasRegistradas([]);
      })
      .finally(() => {
        setCargandoFacturas(false);
      });
  }, [filtrosConsultaQuery]);

  useEffect(() => {
    cargarFacturasRegistradas();
  }, [cargarFacturasRegistradas]);

  const aplicarFiltrosListado = useCallback(() => {
    if (filtroListadoFechaDesde && filtroListadoFechaHasta && filtroListadoFechaDesde > filtroListadoFechaHasta) {
      setModalAviso({
        titulo: "Rango de fechas inválido",
        mensaje: "La fecha inicial no puede ser mayor que la final.",
      });
      return;
    }
    const limiteNumero = Math.min(Math.max(Number(filtroListadoLimite) || 200, 10), 1000);
    setFiltrosConsulta({
      fechaDesde: filtroListadoFechaDesde || undefined,
      fechaHasta: filtroListadoFechaHasta || undefined,
      sucursalId: filtroListadoSucursalId || undefined,
      cajero: filtroListadoCajero.trim() || undefined,
      proveedor: filtroListadoProveedor.trim() || undefined,
      search: filtroListadoBusqueda.trim() || undefined,
      limit: limiteNumero,
    });
  }, [
    filtroListadoFechaDesde,
    filtroListadoFechaHasta,
    filtroListadoSucursalId,
    filtroListadoCajero,
    filtroListadoProveedor,
    filtroListadoBusqueda,
    filtroListadoLimite,
  ]);

  const limpiarFiltrosListado = useCallback(() => {
    setFiltroListadoFechaDesde(fechasIniciales.desde);
    setFiltroListadoFechaHasta(fechasIniciales.hasta);
    setFiltroListadoSucursalId("");
    setFiltroListadoCajero("");
    setFiltroListadoProveedor("");
    setFiltroListadoBusqueda("");
    setFiltroListadoLimite("200");
    setFiltrosConsulta({
      fechaDesde: fechasIniciales.desde,
      fechaHasta: fechasIniciales.hasta,
      sucursalId: undefined,
      cajero: undefined,
      proveedor: undefined,
      search: undefined,
      limit: 200,
    });
  }, [fechasIniciales.desde, fechasIniciales.hasta]);

  useEffect(() => {
    if (errorBusquedaProveedor) {
      setModalAviso({
        titulo: "No pudimos buscar proveedores",
        mensaje: errorBusquedaProveedor,
      });
      setErrorBusquedaProveedor(null);
    }
  }, [errorBusquedaProveedor]);

  useEffect(() => {
    if (errorBusquedaArticulo) {
      setModalAviso({
        titulo: "No pudimos buscar artículos",
        mensaje: errorBusquedaArticulo,
      });
      setErrorBusquedaArticulo(null);
    }
  }, [errorBusquedaArticulo]);

  useEffect(() => {
    if (errorArticulosRecientes) {
      setModalAviso({
        titulo: "No pudimos obtener el historial",
        mensaje: errorArticulosRecientes,
      });
      setErrorArticulosRecientes(null);
    }
  }, [errorArticulosRecientes]);

  const payload = useMemo(() => {
    const contextosControlCaja: Array<{ tipo: "gasto" | "fondo"; monto: number }> = [];
    const montoRendicionNumero = Number(montoRendicion);
    const montoFondoNumero = Number(montoFondo);
    if (controlCajaNumeroValido && aplicaRendicion && Number.isFinite(montoRendicionNumero) && montoRendicionNumero > 0) {
      contextosControlCaja.push({ tipo: "gasto", monto: montoRendicionNumero });
    }
    if (controlCajaNumeroValido && aplicaFondo && Number.isFinite(montoFondoNumero) && montoFondoNumero > 0) {
      contextosControlCaja.push({ tipo: "fondo", monto: montoFondoNumero });
    }
    const controlCajaInfoPayload: ControlCajaInfo | undefined = cajaSeleccionadaInfo
      ? {
          sucursalId:
            typeof cajaSeleccionadaInfo.idsucursal === "number" && Number.isFinite(cajaSeleccionadaInfo.idsucursal)
              ? cajaSeleccionadaInfo.idsucursal
              : null,
          sucursalNombre: cajaSeleccionadaInfo.sucursalNombre ?? cajaSeleccionadaInfo.sucursal ?? null,
          sucursalEtiqueta: describirCaja(cajaSeleccionadaInfo),
          turnoId:
            typeof cajaSeleccionadaInfo.idturno === "number" && Number.isFinite(cajaSeleccionadaInfo.idturno)
              ? cajaSeleccionadaInfo.idturno
              : null,
          turnoDescripcion: cajaSeleccionadaInfo.turno ?? null,
          cajeroId:
            typeof cajaSeleccionadaInfo.idcajero === "number" && Number.isFinite(cajaSeleccionadaInfo.idcajero)
              ? cajaSeleccionadaInfo.idcajero
              : null,
          cajeroNombre: cajaSeleccionadaInfo.cajero ?? cajaSeleccionadaInfo.cajeroRegistrado ?? null,
          fechaApertura: cajaSeleccionadaInfo.fechaApertura ?? cajaSeleccionadaInfo.fechaHoraAperturaTurno ?? null,
          fechaCierre: cajaSeleccionadaInfo.fechaCierre ?? null,
          fechaHoraAperturaTurno: cajaSeleccionadaInfo.fechaHoraAperturaTurno ?? null,
          estadoCaja: cajaSeleccionadaInfo.descEstadoCaja ?? cajaSeleccionadaInfo.estado ?? null,
          estadoTurno: cajaSeleccionadaInfo.descEstadoTurno ?? null,
        }
      : undefined;
    const timbradoNormalizado = timbrado?.trim();
    const timbradoPayload =
      timbradoNormalizado && timbradoNormalizado.length > 0
        ? tipoEmision === "manual"
          ? { numero: timbradoNormalizado, vencimiento: timbradoVencimiento || undefined }
          : { numero: timbradoNormalizado }
        : undefined;
    return {
      proveedor: {
        idpersona: proveedorId,
        ruc: proveedorRuc,
        dv: proveedorDv,
        nombre: proveedorNombre,
        email: proveedorEmail,
        telefono: proveedorTelefono,
      },
      condicionPago,
      comprobante: {
        tipoEmision,
        numero: numeroFactura,
        cdc: tipoEmision === "electronica" ? cdc : undefined,
        timbrado: timbradoPayload,
        fechas: { emision: fechaEmision, recepcion: fechaRecepcion },
      },
      moneda,
      tipoCambio: Number(tipoCambio) || 1,
      items: items.map(({ id, articuloId, articuloReferencia, ...rest }) => rest),
      totales,
      comentario,
      adjuntos: adjuntos.map((file) => ({ nombre: file.name, size: file.size, type: file.type })),
      controlCaja:
        controlCajaNumeroValido && contextosControlCaja.length > 0
          ? {
              idcontrolcaja: controlCajaNumero,
              contextos: contextosControlCaja,
              info: controlCajaInfoPayload,
            }
          : undefined,
    };
  }, [
    proveedorId,
    proveedorRuc,
    proveedorDv,
    proveedorNombre,
    proveedorEmail,
    proveedorTelefono,
    condicionPago,
    tipoEmision,
    numeroFactura,
    cdc,
    timbrado,
    timbradoVencimiento,
    fechaEmision,
    fechaRecepcion,
    moneda,
    tipoCambio,
    items,
    totales,
    comentario,
    adjuntos,
    controlCajaNumeroValido,
    controlCajaNumero,
    controlCajaSeleccionada,
    cajaSeleccionadaInfo,
    aplicaRendicion,
    aplicaFondo,
    montoRendicion,
    montoFondo,
  ]);

  const formatearFechaTabla = useCallback((valor: string) => {
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) {
      return valor;
    }
    return fecha.toLocaleString("es-PY", { dateStyle: "short", timeStyle: "short" });
  }, []);

  const manejarRegistro = useCallback(async () => {
    const montoRendicionNumero = Number(montoRendicion);
    const montoFondoNumero = Number(montoFondo);
    const sumaContextos =
      (aplicaRendicion && Number.isFinite(montoRendicionNumero) ? montoRendicionNumero : 0) +
      (aplicaFondo && Number.isFinite(montoFondoNumero) ? montoFondoNumero : 0);

    if (requiereControlCaja && !controlCajaNumeroValido) {
      setError("Indicá el número de control de caja para asignar la factura.");
      return;
    }
    if (aplicaRendicion && (!Number.isFinite(montoRendicionNumero) || montoRendicionNumero <= 0)) {
      setError("Indicá el monto que corresponde a gastos de rendición.");
      return;
    }
    if (aplicaFondo && (!Number.isFinite(montoFondoNumero) || montoFondoNumero <= 0)) {
      setError("Indicá el monto que corresponde al fondo para sencillo.");
      return;
    }
    if (requiereControlCaja && sumaContextos <= 0) {
      setError("Asigná al menos un monto a la caja seleccionada.");
      return;
    }
    if (totalFactura > 0 && sumaContextos > totalFactura) {
      setError("La suma de los montos asignados a la caja no puede superar el total de la factura.");
      return;
    }
    setEnviando(true);
    setMensaje(null);
    setError(null);
    try {
      const respuesta = await requestJson<{ id?: number }>("/api/compras/registros", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      let idGenerado: number | null = null;
      if (respuesta && typeof respuesta === "object" && "id" in respuesta) {
        const idValor = Number(respuesta.id);
        if (Number.isFinite(idValor)) {
          setRegistroCreadoId(idValor);
          idGenerado = idValor;
        }
      }
      setMensaje("Compra registrada correctamente.");
      setModalExitoAbierto(true);
      cargarFacturasRegistradas();
      setModalFormularioAbierto(false);
      setItems([crearItem()]);
      setAdjuntos([]);
      setComentario("");
      setControlCajaSeleccionada("");
      setAplicaRendicion(false);
      setAplicaFondo(false);
      setMontoRendicion("");
      setMontoFondo("");
      setProveedorId(null);
      setProveedorRuc("");
      setProveedorDv("");
      setProveedorNombre("");
      setProveedorEmail("");
      setProveedorTelefono("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo registrar la compra.");
    } finally {
      setEnviando(false);
    }
  }, [
    payload,
    requiereControlCaja,
    controlCajaNumeroValido,
    aplicaRendicion,
    aplicaFondo,
    montoRendicion,
    montoFondo,
    totalFactura,
    cargarFacturasRegistradas,
  ]);

  return (
    <>
      <Card>
        <CardBody>
          <Nav tabs className="nav-tabs-custom">
            <NavItem>
              <NavLink
                href="#"
                className={tabActiva === "registrar" ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
      setTabActiva("registrar");
                }}
              >
                Registrar factura
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                className={tabActiva === "lista" ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  setTabActiva("lista");
                }}
              >
                Facturas cargadas
              </NavLink>
            </NavItem>
          </Nav>
          <div className="pt-4">
            {tabActiva === "registrar" ? (
              <div className="d-flex flex-column gap-3">
                <p className="text-muted mb-0">
                  Registrá facturas manuales o electrónicas asociadas a una sucursal y caja. Al presionar “Nueva factura”
                  abriremos el formulario completo dentro de un modal.
                </p>
                <div className="d-flex gap-2">
                  <Button color="primary" onClick={abrirFormulario}>
                    <i className="ri-add-line me-1" />
                    Nueva factura
                  </Button>
                  <Button
                    color="link"
                    disabled={facturasRegistradas.length === 0}
                    onClick={() => setTabActiva("lista")}
                  >
                    Ver facturas cargadas ({facturasRegistradas.length})
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 className="mb-0">Facturas registradas</h6>
                    <small className="text-muted">
                      Mostramos {facturasRegistradas.length} resultados según los filtros seleccionados.
                    </small>
                  </div>
                  <Button color="link" size="sm" onClick={cargarFacturasRegistradas} disabled={cargandoFacturas}>
                    <i className="ri-refresh-line me-1" />
                    Recargar
                  </Button>
                </div>
                <div className="border rounded p-3 mb-3 bg-light-subtle">
                  <Row className="gy-3">
                    <Col md={6} lg={3}>
                      <Label className="form-label">Fecha desde</Label>
                      <Input
                        type="date"
                        value={filtroListadoFechaDesde}
                        onChange={(e) => setFiltroListadoFechaDesde(e.target.value)}
                      />
                    </Col>
                    <Col md={6} lg={3}>
                      <Label className="form-label">Fecha hasta</Label>
                      <Input
                        type="date"
                        value={filtroListadoFechaHasta}
                        onChange={(e) => setFiltroListadoFechaHasta(e.target.value)}
                      />
                    </Col>
                    <Col md={6} lg={3}>
                      <Label className="form-label">Sucursal</Label>
                      <Input
                        type="select"
                        value={filtroListadoSucursalId}
                        onChange={(e) => setFiltroListadoSucursalId(e.target.value)}
                      >
                        {sucursalOptions.map((opcion) => (
                          <option key={`filtro-sucursal-${opcion.value}`} value={opcion.value}>
                            {opcion.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={6} lg={3}>
                      <Label className="form-label">Cajero</Label>
                      <Input
                        type="select"
                        value={filtroListadoCajero}
                        onChange={(e) => setFiltroListadoCajero(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {cajeroFiltroOpciones.map((nombre) => (
                          <option key={`filtro-cajero-${nombre}`} value={nombre}>
                            {nombre}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={6} lg={3}>
                      <Label className="form-label">Proveedor</Label>
                      <Input
                        type="select"
                        value={filtroListadoProveedor}
                        onChange={(e) => setFiltroListadoProveedor(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {proveedorFiltroOpciones.map((opcion) => (
                          <option key={`filtro-proveedor-${opcion.value}`} value={opcion.value}>
                            {opcion.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={6} lg={3}>
                      <Label className="form-label">Buscador inteligente</Label>
                      <Input
                        value={filtroListadoBusqueda}
                        onChange={(e) => setFiltroListadoBusqueda(e.target.value)}
                        placeholder="Documento, timbrado, etc."
                      />
                    </Col>
                    <Col md={6} lg={3}>
                      <Label className="form-label">Límite de resultados</Label>
                      <Input
                        type="number"
                        min="10"
                        max="1000"
                        step="10"
                        value={filtroListadoLimite}
                        onChange={(e) => setFiltroListadoLimite(e.target.value)}
                      />
                    </Col>
                    <Col xs={12} className="d-flex justify-content-end gap-2">
                      <Button color="secondary" onClick={limpiarFiltrosListado} disabled={cargandoFacturas}>
                        Limpiar
                      </Button>
                      <Button color="primary" onClick={aplicarFiltrosListado} disabled={cargandoFacturas}>
                        Aplicar filtros
                      </Button>
                    </Col>
                  </Row>
                </div>
                {errorFacturas ? (
                  <Alert color="danger">{errorFacturas}</Alert>
                ) : cargandoFacturas ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" className="me-2" />
                    Cargando facturas...
                  </div>
                ) : facturasRegistradas.length === 0 ? (
                  <Alert color="info" className="mb-0">
                    No encontramos facturas cargadas con los filtros actuales.
                  </Alert>
                ) : (
                  <div className="table-responsive" style={{ maxHeight: 420, overflowY: "auto" }}>
                    <Table hover className="align-middle mb-0">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Proveedor</th>
                          <th>Documento y timbrado</th>
                          <th>Control caja y sucursal</th>
                          <th className="text-center">Items</th>
                          <th className="text-end">Totales</th>
                          <th>Fechas</th>
                          <th>Estado</th>
                          <th>Usuario y registro</th>
                        </tr>
                      </thead>
                      <tbody>
                        {facturasRegistradas.map((factura) => (
                          <tr key={factura.id}>
                            <td className="fw-semibold">{factura.id}</td>
                            <td>
                              <div className="fw-semibold">{factura.proveedor ?? "-"}</div>
                              <small className="text-muted">
                                {factura.proveedorDocumento ? `RUC: ${factura.proveedorDocumento}` : "Sin RUC"}
                              </small>
                            </td>
                            <td>
                              <div>{factura.numeroDocumento}</div>
                              <small className="text-muted d-block">{factura.condicionPago.toUpperCase()}</small>
                              <div className="mt-1">
                                Timbrado: <strong>{factura.timbrado ?? "-"}</strong>
                              </div>
                              {factura.timbradoVencimiento ? (
                                <small className="text-muted">
                                  Venc.: {formatearFechaTabla(factura.timbradoVencimiento)}
                                </small>
                              ) : null}
                            </td>
                            <td>
                              <div className="fw-semibold">
                                {factura.controlCajaId ? `Caja #${factura.controlCajaId}` : "Sin caja"}
                              </div>
                              {factura.controlCajaInfo?.sucursalNombre ? (
                                <small className="text-muted d-block">
                                  {factura.controlCajaInfo.sucursalNombre}
                                  {factura.controlCajaInfo.sucursalId ? ` · #${factura.controlCajaInfo.sucursalId}` : ""}
                                </small>
                              ) : null}
                              {factura.controlCajaInfo?.cajeroNombre ? (
                                <small className="text-muted d-block">
                                  Cajero: {factura.controlCajaInfo.cajeroNombre}
                                </small>
                              ) : null}
                              {factura.controlCajaInfo?.turnoDescripcion ? (
                                <small className="text-muted d-block">
                                  Turno: {factura.controlCajaInfo.turnoDescripcion}
                                </small>
                              ) : null}
                            </td>
                            <td className="text-center">{factura.itemsCantidad ?? 0}</td>
                            <td className="text-end">
                              <div>Grav. 10%: {formatearMonto(factura.totalGravada10 ?? 0)}</div>
                              <div>Grav. 5%: {formatearMonto(factura.totalGravada5 ?? 0)}</div>
                              <div>Exenta: {formatearMonto(factura.totalExenta ?? 0)}</div>
                              <div className="fw-semibold mt-1">
                                Total: {factura.moneda} {formatearMonto(factura.total)}
                              </div>
                            </td>
                            <td>
                              <div>Emisión: {factura.fechaEmision ? formatearFechaTabla(factura.fechaEmision) : "-"}</div>
                              <div>Recepción: {factura.fechaRecepcion ? formatearFechaTabla(factura.fechaRecepcion) : "-"}</div>
                            </td>
                            <td>{factura.estado ?? "-"}</td>
                            <td>
                              <div>{factura.usuarioNombre ?? (factura.usuarioId ? `Usuario #${factura.usuarioId}` : "-")}</div>
                              <small className="text-muted">
                                {formatearFechaTabla(factura.createdAt)}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </div>
        </CardBody>
      </Card>

      <Modal
        isOpen={modalFormularioAbierto}
        toggle={cerrarFormulario}
        size="xl"
      >
        <ModalHeader toggle={cerrarFormulario}>Registrar compra</ModalHeader>
        <ModalBody>
          <Form onSubmit={(e) => e.preventDefault()}>
            {mensaje ? <Alert color="success">{mensaje}</Alert> : null}
            {error ? <Alert color="danger">{error}</Alert> : null}

            <div className="mb-4">
              <h4 className="mb-1">Asociación con control de caja</h4>
              <p className="text-muted mb-3">
                Seleccioná la sucursal o caja a la que querés asociar la factura (solo si aplica a rendiciones o fondos).
              </p>
              <Row className="gy-3">
                <Col md={6}>
                  <Label className="form-label">Sucursal</Label>
                  <Input
                    type="select"
                    value={sucursalFiltro}
                    onChange={(e) => setSucursalFiltro(e.target.value)}
                    disabled={cargandoSucursales}
                  >
                    {sucursalOptions.map((opcion) => {
                      const detalle = opcion.descripcionFuente ? ` · ${opcion.descripcionFuente}` : "";
                      return (
                        <option key={opcion.value} value={opcion.value}>
                          {opcion.label}
                          {detalle}
                        </option>
                      );
                    })}
                  </Input>
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small className={errorSucursales ? "text-danger" : "text-muted"}>
                      {errorSucursales ??
                        (cargandoSucursales ? "Cargando sucursales..." : "Mostramos todas las sucursales habilitadas.")}
                    </small>
                    {onRecargarSucursales ? (
                      <Button
                        type="button"
                        color="link"
                        size="sm"
                        onClick={onRecargarSucursales}
                        disabled={cargandoSucursales}
                      >
                        Actualizar
                      </Button>
                    ) : null}
                  </div>
                </Col>
                <Col md={6}>
                  <Label className="form-label">Caja disponible</Label>
                  <Input
                    type="select"
                    value={controlCajaSeleccionada}
                    onChange={(e) => setControlCajaSeleccionada(e.target.value)}
                    disabled={cargandoCajas}
                  >
                    <option value="">Seleccioná una caja</option>
                    {opcionesCajas.map((opcion) => (
                      <option key={opcion.value} value={opcion.value}>
                        {opcion.label}
                      </option>
                    ))}
                  </Input>
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small className={errorCajas ? "text-danger" : "text-muted"}>
                      {errorCajas ??
                        (cargandoCajas
                          ? "Cargando cajas..."
                          : opcionesCajas.length === 0
                            ? `No encontramos cajas abiertas para la sucursal seleccionada${
                                descripcionFuenteSeleccionada ? ` (${descripcionFuenteSeleccionada})` : ""
                              }.`
                            : "Ordenamos las cajas de la más reciente a la más antigua.")}
                    </small>
                    {onRecargarCajas ? (
                      <Button
                        type="button"
                        color="link"
                        size="sm"
                        onClick={onRecargarCajas}
                        disabled={cargandoCajas}
                      >
                        Actualizar
                      </Button>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </div>

            <div className="mb-4">
            <h4 className="mb-1">Datos del comprobante</h4>
            <div className="d-flex flex-wrap gap-4">
              <div>
                <Label className="form-label d-block">Tipo de emisión</Label>
                <div className="d-flex gap-4">
                  <div className="form-check">
                    <Input
                      type="radio"
                      id="emisionManual"
                      name="tipoEmision"
                      value="manual"
                      checked={tipoEmision === "manual"}
                      onChange={() => setTipoEmision("manual")}
                    />
                    <Label className="form-check-label" htmlFor="emisionManual">
                      Impresa
                    </Label>
                  </div>
                  <div className="form-check">
                    <Input
                      type="radio"
                      id="emisionElectronica"
                      name="tipoEmision"
                      value="electronica"
                      checked={tipoEmision === "electronica"}
                      onChange={() => setTipoEmision("electronica")}
                    />
                    <Label className="form-check-label" htmlFor="emisionElectronica">
                      Electrónica
                    </Label>
                  </div>
                </div>
              </div>
              <div>
                <Label className="form-label d-block">Condición de pago</Label>
                <div className="d-flex gap-4">
                  <div className="form-check">
                    <Input
                      type="radio"
                      id="pagoContado"
                      name="condicionPago"
                      value="contado"
                      checked={condicionPago === "contado"}
                      onChange={() => setCondicionPago("contado")}
                    />
                    <Label className="form-check-label" htmlFor="pagoContado">
                      Contado
                    </Label>
                  </div>
                  <div className="form-check">
                    <Input
                      type="radio"
                      id="pagoCredito"
                      name="condicionPago"
                      value="credito"
                      checked={condicionPago === "credito"}
                      onChange={() => setCondicionPago("credito")}
                    />
                    <Label className="form-check-label" htmlFor="pagoCredito">
                      Crédito
                    </Label>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-3 align-items-end mt-3">
              <div className="flex-fill" style={{ minWidth: 150 }}>
                <Label className="form-label">Moneda</Label>
                <Input type="select" value={moneda} onChange={(e) => setMoneda(e.target.value)}>
                  {MONEDAS.map((opcion) => (
                    <option key={opcion.value} value={opcion.value}>
                      {opcion.label}
                    </option>
                  ))}
                </Input>
              </div>
              <div className="flex-fill" style={{ minWidth: 150 }}>
                <Label className="form-label">Tipo de cambio</Label>
                <Input
                  value={tipoCambio}
                  onChange={(e) => setTipoCambio(e.target.value)}
                  disabled={esMonedaLocal}
                  inputMode="decimal"
                  placeholder="1"
                />
              </div>
              <div className="flex-fill" style={{ minWidth: 150 }}>
                <Label className="form-label">Timbrado</Label>
                <Input
                  value={timbrado}
                  onChange={(e) => setTimbrado(e.target.value)}
                  placeholder="12345678"
                />
              </div>
              <div className="flex-fill" style={{ minWidth: 150 }}>
                <Label className="form-label">Vencimiento</Label>
                <Input
                  type="date"
                  value={timbradoVencimiento}
                  onChange={(e) => setTimbradoVencimiento(e.target.value)}
                  disabled={tipoEmision !== "manual"}
                />
              </div>
              <div className="flex-fill" style={{ minWidth: 180 }}>
                <Label className="form-label">Fecha de emisión</Label>
                <Input type="date" value={fechaEmision} onChange={(e) => setFechaEmision(e.target.value)} />
              </div>
              <div className="flex-fill" style={{ minWidth: 180 }}>
                <Label className="form-label">Fecha de recepción</Label>
                <Input type="date" value={fechaRecepcion} onChange={(e) => setFechaRecepcion(e.target.value)} />
              </div>
              <div className="flex-fill" style={{ minWidth: 220 }}>
                <Label className="form-label">Número de factura</Label>
                <Input
                  value={numeroFacturaCadena}
                  onChange={(e) => actualizarNumeroFacturaCadena(e.target.value)}
                  placeholder="001-001-0000001"
                  invalid={!numeroFacturaValido && numeroFacturaCadena.length > 0}
                />
                <FormFeedback>Completá 3-3-7 dígitos (Est. · Punto · Número).</FormFeedback>
              </div>
            </div>
            {tipoEmision === "electronica" ? (
              <div className="mt-3" style={{ maxWidth: 420 }}>
                <Label className="form-label">CDC (código de control)</Label>
                <Input value={cdc} onChange={(e) => setCdc(e.target.value)} placeholder="Ingresa el CDC completo" />
              </div>
            ) : null}
          </div>

          <div className="mb-4">
            <h4 className="mb-1">Detalle de artículos o servicios</h4>
            <p className="text-muted mb-3">Carga línea por línea y calculamos automáticamente las gravadas y el IVA.</p>
            <div className="small text-muted mb-2">Total ítems: {items.length}</div>
            <div className="table-responsive">
              <Table bordered hover responsive className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Descripción</th>
                    <th style={{ width: 200 }}>Cantidad</th>
                    <th style={{ width: 160 }}>Costo unitario</th>
                    <th style={{ width: 140 }}>IVA</th>
                    <th style={{ width: 170 }} className="text-end">
                      Subtotal
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const subtotal = (Number(item.cantidad) || 0) * (Number(item.costoUnitario) || 0);
                    // Solo permitimos editar cantidad/costo cuando ya hay un artículo asociado.
                    const articuloSeleccionado = Boolean(item.articuloId);
                    return (
                      <tr key={item.id}>
                        <td>
                          <Button
                            type="button"
                            color="light"
                            className="w-100 text-start border d-flex align-items-center justify-content-between"
                            onClick={() => abrirModalArticulo(item.id)}
                            disabled={!permiteBusquedaArticulo}
                            title={
                              permiteBusquedaArticulo
                                ? "Seleccioná un artículo o servicio desde el buscador"
                                : "Seleccioná primero una sucursal o caja"
                            }
                          >
                            <div>
                              <div className={item.descripcion ? "text-body" : "text-muted fst-italic"}>
                                {item.descripcion || "Seleccionar artículo o servicio"}
                              </div>
                              {item.articuloId || item.articuloReferencia ? (
                                <small className="text-muted">
                                  {[
                                    item.articuloId ? `ID ${item.articuloId}` : null,
                                    item.articuloReferencia ? `Ref. ${item.articuloReferencia}` : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </small>
                              ) : null}
                            </div>
                            <i className="ri-search-line text-muted ms-2" />
                          </Button>
                        </td>
                        <td>
                          <InputGroup>
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={item.cantidad}
                              disabled={!articuloSeleccionado}
                              onChange={(e) => actualizarItem(item.id, { cantidad: Number(e.target.value) || 0 })}
                            />
                            <Button
                              type="button"
                              color="secondary"
                              size="sm"
                              className="d-flex align-items-center"
                              style={{ backgroundColor: "#f0f4ff", borderColor: "#d3ddff", color: "#2b3a67" }}
                              title="Calcular desde total"
                              onClick={() => abrirCalculadora(item)}
                              disabled={!articuloSeleccionado}
                            >
                              <i className="ri-calculator-line me-1" />
                              Calcular
                            </Button>
                          </InputGroup>
                        </td>
                        <td>
                          <Input
                            type="number"
                            min="0"
                            step="1000"
                            inputMode="decimal"
                            value={item.costoUnitario}
                            disabled={!articuloSeleccionado}
                            onChange={(e) =>
                              actualizarItem(item.id, { costoUnitario: Number(e.target.value) || 0 })
                            }
                          />
                        </td>
                        <td className="text-center align-middle">
                          <div
                            className="form-control bg-light text-muted border-0 fw-semibold"
                            style={{ fontStyle: "italic" }}
                          >
                            {item.iva === "0" ? "Exenta" : item.iva === "5" ? "IVA 5%" : "IVA 10%"}
                          </div>
                        </td>
                        <td className="text-end">
                          <div className="fw-semibold">{formatearMonto(subtotal)}</div>
                          <Button
                            type="button"
                            color="link"
                            size="sm"
                            className="text-danger p-0 mt-1"
                            onClick={() => eliminarItem(item.id)}
                            disabled={items.length === 1}
                          >
                            Eliminar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mt-3">
              <Button type="button" color="link" onClick={agregarItem} className="px-0">
                <i className="ri-add-line me-1" />
                Agregar ítem
              </Button>
              <div className="text-end small">
                <div>Gravada 10%: <strong>{formatearMonto(totales.gravada10)}</strong></div>
                <div>IVA 10%: <strong>{formatearMonto(totales.iva10)}</strong></div>
                <div>Gravada 5%: <strong>{formatearMonto(totales.gravada5)}</strong></div>
                <div>IVA 5%: <strong>{formatearMonto(totales.iva5)}</strong></div>
                <div>Exentas: <strong>{formatearMonto(totales.exenta)}</strong></div>
                <div className="fs-5 mt-2">
                  Total general: <strong>{formatearMonto(totalFactura)}</strong>
                </div>
              </div>
            </div>
            <div className="text-muted small mt-2">
              Seleccioná un artículo para habilitar la carga de cantidades y costos unitarios.
            </div>
          </div>

          <div className="mb-4">
            <h4 className="mb-1">Asignaciones de la factura</h4>
            <p className="text-muted mb-3">
              Distribuí el importe entre gastos de la rendición y fondo para sencillo cuando corresponda.
            </p>
            <Table responsive bordered size="sm" className="align-middle">
              <thead className="table-light">
                <tr>
                  <th>Asociación</th>
                  <th className="text-end" style={{ width: 220 }}>
                    Monto
                  </th>
                  <th style={{ width: 160 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div className="fw-semibold">Gastos de la rendición</div>
                    <div className="text-muted small">
                      Distribuí el total de la factura entre los gastos rendidos según corresponda.
                    </div>
                  </td>
                  <td className="text-end">
                    {aplicaRendicion ? (
                      <Input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="1000"
                        placeholder="0"
                        className="text-end"
                        value={montoRendicion}
                        max={maximoMontoRendicion > 0 ? maximoMontoRendicion : undefined}
                        onChange={(e) => setMontoRendicion(e.target.value)}
                      />
                    ) : (
                      <span className="text-muted small">Sin asignar</span>
                    )}
                  </td>
                  <td>
                    <div className="form-check form-switch mb-0">
                      <Input
                        type="switch"
                        id="asignarRendicion"
                        checked={aplicaRendicion}
                        disabled={!controlCajaNumeroValido}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setAplicaRendicion(checked);
                          if (!checked) {
                            setMontoRendicion("");
                          }
                        }}
                      />
                      <Label className="form-check-label" htmlFor="asignarRendicion">
                        {aplicaRendicion ? "Activo" : "Asignar"}
                      </Label>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className="fw-semibold">Fondo para sencillo</div>
                    <div className="text-muted small">Asigná el monto que corresponde al fondo de cambio.</div>
                  </td>
                  <td className="text-end">
                    {aplicaFondo ? (
                      <Input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="1000"
                        placeholder="0"
                        className="text-end"
                        value={montoFondo}
                        max={maximoMontoFondo > 0 ? maximoMontoFondo : undefined}
                        onChange={(e) => setMontoFondo(e.target.value)}
                      />
                    ) : (
                      <span className="text-muted small">Sin asignar</span>
                    )}
                  </td>
                  <td>
                    <div className="form-check form-switch mb-0">
                      <Input
                        type="switch"
                        id="asignarFondo"
                        checked={aplicaFondo}
                        disabled={!controlCajaNumeroValido}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setAplicaFondo(checked);
                          if (!checked) {
                            setMontoFondo("");
                          }
                        }}
                      />
                      <Label className="form-check-label" htmlFor="asignarFondo">
                        {aplicaFondo ? "Activo" : "Asignar"}
                      </Label>
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
            <div className="d-flex justify-content-between flex-wrap gap-2 mt-2 small">
              <span>
                Montos asignados: <strong>{formatearMonto(totalAsignado)}</strong>
              </span>
              <span>
                Total general: <strong>{formatearMonto(totalFactura)}</strong>
              </span>
            </div>
            {excesoAsignacion ? (
              <Alert color="danger" className="mt-3 mb-0">
                La suma de montos asignados no puede ser mayor al total de la factura.
              </Alert>
            ) : null}
          </div>

          <div className="mb-4">
            <h4 className="mb-1">Adjuntos y confirmación</h4>
            <p className="text-muted mb-3">Sumá los comprobantes digitales y notas internas antes de confirmar.</p>
            <Row className="gy-3">
              <Col md={6}>
                <Label className="form-label">Adjuntar archivos</Label>
                <Input type="file" multiple onChange={manejarAdjuntos} />
                {adjuntos.length > 0 ? (
                  <ul className="list-unstyled small text-muted mt-2 mb-0">
                    {adjuntos.map((file) => (
                      <li key={file.name}>
                        <i className="ri-attachment-2 me-1" />
                        {file.name} ({Math.round(file.size / 1024)} KB)
                      </li>
                    ))}
                  </ul>
                ) : (
                  <small className="text-muted">Aceptamos PDF, XML o imágenes escaneadas.</small>
                )}
              </Col>
              <Col md={6}>
                <Label className="form-label">Comentario interno</Label>
                <Input
                  type="textarea"
                  rows={3}
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Notas sobre centro de costo, responsable, etc."
                />
              </Col>
            </Row>
          </div>

          <div className="d-flex gap-3">
            <Button
              color="success"
              className="btn-label right ms-auto"
              onClick={manejarRegistro}
              disabled={enviando || (requiereControlCaja && !controlCajaNumeroValido)}
            >
              <i className="ri-checkbox-circle-line label-icon align-middle fs-16 ms-2" />
              {enviando ? "Registrando..." : "Registrar compra"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
        <Modal isOpen={modalProveedorAbierto} toggle={cerrarModalProveedor} size="lg">
          <ModalHeader toggle={cerrarModalProveedor}>Buscar proveedor</ModalHeader>
          <ModalBody>
            <Label className="form-label">Buscar en base histórica</Label>
            <Input
              type="search"
              value={busquedaProveedor}
              onChange={(e) => setBusquedaProveedor(e.target.value)}
              placeholder="Escribí RUC, razón social o nombre"
              disabled={!sucursalBusquedaId}
            />
            <small className="text-muted">
              {sucursalBusquedaId
                ? `Buscamos en ${descripcionFuenteBusqueda ?? `la sucursal ${sucursalBusquedaLabel ?? sucursalBusquedaId}`}. Escribí al menos 3 caracteres.`
                : "Seleccioná una sucursal o caja para habilitar el buscador."}
            </small>
            <div className="mt-3">
              {buscandoProveedor ? (
                <div className="d-flex align-items-center gap-2 text-muted small">
                  <Spinner size="sm" /> Buscando proveedores...
                </div>
              ) : null}
              {!buscandoProveedor && modalProveedorAbierto && busquedaProveedor.trim().length >= 3 && resultadosProveedor.length === 0 ? (
                <Alert color="warning" className="mt-2 mb-0">
                  No encontramos proveedores que coincidan con tu búsqueda.
                </Alert>
              ) : null}
              {resultadosProveedor.length > 0 ? (
                <div className="table-responsive mt-3">
                  <Table size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Proveedor</th>
                        <th>RUC</th>
                        <th>Email</th>
                        <th>Teléfono</th>
                        <th style={{ width: 130 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {resultadosProveedor.map((resultado, index) => {
                        const razon =
                          resultado.razonSocial && resultado.razonSocial.trim().length > 0
                            ? resultado.razonSocial
                            : resultado.persona && resultado.persona.trim().length > 0
                            ? resultado.persona
                            : [resultado.nombre, resultado.apellido]
                                .filter((parte) => typeof parte === "string" && parte.trim().length > 0)
                                .join(" ");
                        const rucBase = resultado.rucNumero ?? resultado.ruc ?? "";
                        const rucMostrar =
                          rucBase && resultado.dv ? `${rucBase}-${resultado.dv}` : rucBase || resultado.ruc || "-";
                        const key = resultado.idpersona ?? `${rucBase}-${index}`;
                        return (
                          <tr key={key}>
                            <td>{razon || "-"}</td>
                            <td>{rucMostrar || "-"}</td>
                            <td>{resultado.email || "-"}</td>
                            <td>{resultado.telefono || "-"}</td>
                            <td className="text-end">
                              <Button
                                size="sm"
                                color="success"
                                type="button"
                                className="text-white"
                                style={{ backgroundColor: "#13c56b", borderColor: "#0f9b53" }}
                                onClick={() => seleccionarProveedor(resultado)}
                              >
                                Usar datos
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={cerrarModalProveedor}>
              Cerrar
            </Button>
          </ModalFooter>
        </Modal>
        <Modal isOpen={modalArticuloAbierto} toggle={cerrarModalArticulo} size="lg">
          <ModalHeader toggle={cerrarModalArticulo}>Buscar artículo</ModalHeader>
          <ModalBody>
            <Label className="form-label">Buscar en inventario histórico</Label>
            <Input
              type="search"
              value={busquedaArticulo}
              onChange={(e) => setBusquedaArticulo(e.target.value)}
              placeholder="Escribí código, descripción o referencia"
              disabled={!permiteBusquedaArticulo}
            />
            <small className="text-muted">
              {permiteBusquedaArticulo
                ? `Buscamos artículos en ${descripcionFuenteBusqueda ?? "la base seleccionada"}. Escribí al menos 3 caracteres.`
                : "Seleccioná una sucursal o caja para habilitar el buscador."}
            </small>
            <div className="mt-3 d-flex gap-2 align-items-center flex-wrap">
              <Button
                type="button"
                color="secondary"
                outline
                size="sm"
                disabled={!articulosRecientes.length || cargandoArticulosRecientes}
                onClick={() => setMostrarHistorialCompleto((prev) => !prev)}
              >
                <i className="ri-time-line me-1" />
                {mostrarHistorialCompleto ? "Ocultar últimos 6 meses" : "Ver últimos 6 meses de compra"}
              </Button>
              {cargandoArticulosRecientes ? (
                <div className="text-muted small d-flex align-items-center gap-1">
                  <Spinner size="sm" /> Cargando historial...
                </div>
              ) : null}
              {!cargandoArticulosRecientes && articulosRecientes.length === 0 ? (
                <small className="text-muted">
                  Todavía no encontramos compras recientes para el proveedor seleccionado.
                </small>
              ) : null}
            </div>
            <div className="mt-3">
              {buscandoArticulo ? (
                <div className="d-flex align-items-center gap-2 text-muted small">
                  <Spinner size="sm" /> Buscando artículos...
                </div>
              ) : null}
              {!buscandoArticulo &&
              modalArticuloAbierto &&
              busquedaArticulo.trim().length >= 3 &&
              resultadosArticulo.length === 0 ? (
                <div className="text-muted small mt-2">
                  No encontramos artículos que coincidan con tu búsqueda.
                </div>
              ) : null}
              {mostrarHistorialCompleto && articulosRecientes.length > 0 ? (
                <div className="mt-3">
                  <h6 className="text-muted">
                    Usados en compras recientes {proveedorNombre ? `con ${proveedorNombre}` : ""}
                  </h6>
                  <div className="table-responsive mt-2" style={{ maxHeight: 360, overflowY: "auto" }}>
                    <Table size="sm" bordered className="align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Artículo</th>
                          <th>Unidad</th>
                          <th className="text-end">Costo unitario</th>
                          <th className="text-end">Monto total</th>
                          <th>Última factura</th>
                          <th style={{ width: 130 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {articulosRecientes.map((historial, index) => (
                          <tr key={`${historial.idarticulo ?? "hist"}-${index}`}>
                            <td>
                              <div className="fw-semibold">
                                {historial.descripcion ?? historial.idarticulo ?? "-"}
                              </div>
                              <div className="text-muted small">
                                {[
                                  historial.idarticulo ? `ID ${historial.idarticulo}` : null,
                                  historial.serverOrigen ? historial.serverOrigen : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </div>
                            </td>
                            <td>{historial.unidadMedida ?? "-"}</td>
                            <td className="text-end">
                              {formatearMonto(
                                Number.isFinite(historial.costoUnitarioConIva ?? 0)
                                  ? Number(historial.costoUnitarioConIva ?? 0)
                                  : 0,
                              )}
                            </td>
                            <td className="text-end">
                              {formatearMonto(
                                Number.isFinite(historial.importeTotal ?? 0)
                                  ? Number(historial.importeTotal ?? 0)
                                  : 0,
                              )}
                            </td>
                            <td>
                              <div>{historial.numeroFactura ?? "-"}</div>
                              {historial.fecha ? (
                                <small className="text-muted">
                                  {new Date(historial.fecha).toLocaleDateString("es-PY")}
                                </small>
                              ) : null}
                            </td>
                            <td className="text-end">
                              <Button
                                size="sm"
                                color="success"
                                type="button"
                                className="text-white"
                                style={{ backgroundColor: "#13c56b", borderColor: "#0f9b53" }}
                                onClick={() => seleccionarArticuloDesdeHistorial(historial)}
                              >
                                Usar ítem
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              ) : null}
              {resultadosArticulo.length > 0 ? (
                <div className="table-responsive mt-3" style={{ maxHeight: 360, overflowY: "auto" }}>
                  <Table size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th>Referencia</th>
                        <th className="text-end">Costo unitario</th>
                        <th className="text-center">IVA sugerido</th>
                        <th style={{ width: 130 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {resultadosArticulo.map((articulo, index) => {
                        const codigo = articulo.idarticulo ?? `ART-${index}`;
                        const descripcion =
                          articulo.descripcion ??
                          articulo.descripcionCorta ??
                          articulo.referencia ??
                          "-";
                        const referencia = articulo.referencia ?? articulo.barcode ?? "-";
                        const costo =
                          articulo.costoPromedio ??
                          articulo.precioSugerido ??
                          0;
                        const iva = inferirIvaDesdeArticulo(articulo);
                        return (
                          <tr key={codigo}>
                            <td>{articulo.idarticulo ?? "-"}</td>
                            <td>{descripcion}</td>
                            <td>{referencia}</td>
                            <td className="text-end">
                              {formatearMonto(Number.isFinite(costo) ? Number(costo) : 0)}
                            </td>
                            <td className="text-center">{iva === "0" ? "Exenta" : `IVA ${iva}%`}</td>
                            <td className="text-end">
                              <Button
                                size="sm"
                                color="success"
                                type="button"
                                className="text-white"
                                style={{ backgroundColor: "#13c56b", borderColor: "#0f9b53" }}
                                onClick={() => seleccionarArticulo(articulo)}
                              >
                                Usar ítem
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={cerrarModalArticulo}>
              Cerrar
            </Button>
          </ModalFooter>
        </Modal>
        <Modal isOpen={modalCalculadoraAbierto} toggle={cerrarCalculadora}>
          <ModalHeader toggle={cerrarCalculadora}>Calcular costo unitario</ModalHeader>
          <ModalBody>
            <p className="text-muted">
              Ingresá la cantidad y el total del ítem para calcular automáticamente el costo unitario.
            </p>
            <Row className="gy-3">
              <Col md={6}>
                <Label className="form-label">Cantidad</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={calculadoraCantidad}
                  onChange={(e) => setCalculadoraCantidad(e.target.value)}
                />
              </Col>
              <Col md={6}>
                <Label className="form-label">Total</Label>
                <Input
                  type="number"
                  min="0"
                  step="100"
                  value={calculadoraTotal}
                  onChange={(e) => setCalculadoraTotal(e.target.value)}
                />
              </Col>
            </Row>
            {errorCalculadora ? (
              <div className="text-danger mt-3">{errorCalculadora}</div>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={cerrarCalculadora}>
              Cancelar
            </Button>
            <Button color="primary" onClick={aplicarCalculadora}>
              Aplicar
            </Button>
          </ModalFooter>
        </Modal>
        <Modal isOpen={modalExitoAbierto} toggle={() => setModalExitoAbierto(false)}>
          <ModalHeader toggle={() => setModalExitoAbierto(false)}>Compra registrada</ModalHeader>
          <ModalBody>
            <div className="text-center">
              <i className="ri-checkbox-circle-line text-success display-4 mb-3" />
              <p>La compra se guardó correctamente.</p>
              {registroCreadoId ? (
                <p className="text-muted">
                  ID de registro: <strong>{registroCreadoId}</strong>
                </p>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter className="justify-content-center">
            <Button color="primary" onClick={() => setModalExitoAbierto(false)}>
              Entendido
            </Button>
          </ModalFooter>
        </Modal>
        {modalAviso ? (
          <Modal isOpen toggle={() => setModalAviso(null)}>
            <ModalHeader toggle={() => setModalAviso(null)}>{modalAviso.titulo}</ModalHeader>
            <ModalBody>{modalAviso.mensaje}</ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={() => setModalAviso(null)}>
                Entendido
              </Button>
            </ModalFooter>
          </Modal>
        ) : null}
      </>
  );
};

export default RegistroComprasStepper;
